import { clsx } from 'clsx';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { matchPath } from 'react-router-dom';

import { DEFAULT_YEAR_FORMAT, NotFoundContentDropdown } from '@/shared';
import { TECH_CARD_RUBBER_NEW, UserRoles } from '@/shared/core';
import { useAppSelector } from '@/shared/state';
import { MainBlockRubberTypes } from './main.types';

import { chakra, Flex, Input, Table, Td, Text, Th, Tr, useToast } from '@chakra-ui-kraud/react';
import { DatePicker, Select } from 'antd';
import _debounce from 'lodash/debounce';

import defaultStyles from '../../tech-card.module.scss';
import styles from './main.module.scss';
import { OVorDateSelectOption } from '../../../../shared/core/interfaces/tech-card-interfaces';
import { FileDomain, useGetNumsQuery, useGetRubbersQuery, useLazyGetRubbersQuery } from '@/shared/state/api/swagger';
import { RubberTechMapFormData } from '@/pages/tech-card-page/form-config-rubber';

export const MainBlockRubber: FC<MainBlockRubberTypes> = ({
	cardInfo,
	isEditable,
	focusedBlock,
	materials,
	setFocusedBlock,
}) => {
	const toast = useToast();

	const userRole = useAppSelector((state) => state.auth.userProfile?.role);

	const { control, setValue, getValues } = useFormContext<RubberTechMapFormData>();
	const selectedRubber = useWatch({ name: 'custom_rubber', control });

	const isCreationMode = !!matchPath(TECH_CARD_RUBBER_NEW, location.pathname) && !cardInfo?.number;
	const rubberRef = useRef<HTMLTableCellElement>(null);

	const [rubbersQuery, setRubbersQuery] = useState('');
	const [numsQuery, setNumsQuery] = useState('');

	const [trigger, { isFetching: isRubbersCatalogFetching }] = useLazyGetRubbersQuery();
	const { data: rubbersData, isFetching: isRubbersDataFetching } = useGetRubbersQuery({
		stringToSearch: rubbersQuery,
	});
	const { data: numsData } = useGetNumsQuery({
		stringToSearch: numsQuery,
		standardConsumptionType: 'grams',
	});

	const { rubbers, selectedRubbers } = useMemo(() => {
		return {
			rubbers: rubbersData?.payload,
			selectedRubbers: rubbersData?.payload?.map((rubber) => ({
				value: rubber.id,
				label: `${rubber.symbol || 'Нет обозначения'} (${rubber.name || '-'})`,
			})) || [{ value: 0, label: '' }],
		};
	}, [rubbersData]);
	const { nums, selectedNum } = useMemo(() => {
		return {
			nums: numsData?.payload,
			selectedNum: numsData?.payload?.map((num) => ({
				value: num.id,
				label: num.num,
			})),
		};
	}, [numsData]);

	const debouncedRubbersSearch = _debounce((search) => setRubbersQuery(search), 700);
	const debouncedNumsSearch = _debounce((search) => setNumsQuery(search), 700);

	const [OVorDateSelectOptions, setOVorDateSelectOptions] = useState<OVorDateSelectOption[]>(
		[
			(cardInfo?.rubber_num?.o_v || getValues('rubber_num.o_v')) && {
				value: 'o_v',
				label: cardInfo?.rubber_num?.o_v || getValues('rubber_num.o_v'),
			},
			(cardInfo?.rubber_num?.date_of_manufacture || getValues('rubber_num.date_of_manufacture')) && {
				value: 'date_of_manufacture',
				label: dayjs(
					cardInfo?.rubber_num?.date_of_manufacture || getValues('rubber_num.date_of_manufacture'),
				).format(DEFAULT_YEAR_FORMAT),
			},
			{ value: 'empty', label: '-' },
		].filter(Boolean) as OVorDateSelectOption[], // Удаляет undefined элементы
	);

	const handleRubberSelect = (value: string | number) => {
		setValue('rubber_operations', []);
		const selectedRubber = rubbers?.find((rubber) => rubber.id === Number(value));
		const selectedMaterial = materials?.find((material) => material.id === selectedRubber?.material_id);

		if (selectedRubber?.material_id) {
			setValue(
				'custom_rubber',
				{
					...selectedRubber,
					file: selectedRubber?.file as FileDomain,
					id: selectedRubber?.id ?? 0,
					symbol: selectedRubber?.symbol || '',
					extra_symbol: selectedRubber?.extra_symbol || '',
					extra_name: selectedRubber?.extra_name,
					name: selectedRubber?.name || '-',
					approval_card: selectedRubber?.approval_card || '',
					extra_approval_card: selectedRubber?.extra_approval_card || '',
					rubber_catalog_id: selectedRubber?.id,
				},
				{ shouldValidate: true },
			);
		}

		setValue(
			'custom_rubber.material',
			{
				id: selectedMaterial?.id || 0,
				symbol: selectedMaterial?.symbol || '-',
				gost: selectedMaterial?.gost || '-',
				sortament: selectedMaterial?.sortament || '-',
			},
			{ shouldValidate: true },
		);

		setValue(
			'rubber_num',
			{
				id: undefined,
				num: undefined,
				number_of_melt: '',
				o_v: '',
				date_of_manufacture: '',
				symbol: '',
				sortament: '',
				gost: '',
				gost_na_sortament: '',
				o_v_or_date_of_manufacture: 'empty',
				standard_consumption_type: undefined,
			},
			{ shouldValidate: true },
		);
		setValue('rubber_plan.NR_grams', undefined, {
			shouldValidate: true,
		});

		setNumsQuery('');

		const sortedOperationsTypes = [...(selectedRubber?.operation_types ?? [])]?.sort(
			(a, b) => (a.step_id ?? 0) - (b.step_id ?? 0),
		);
		sortedOperationsTypes?.forEach((operation, index) => {
			setValue(`rubber_operations.${index}.step_id`, operation.step_id ?? 0);
			setValue(`rubber_operations.${index}.name`, operation.name || '');
		});
	};

	const handleNumSelect = useCallback(
		async (value: number) => {
			const selectedNum = nums?.find((num) => num.id === value);

			let catalogProduct: RubberTechMapFormData['custom_rubber'] | undefined = selectedRubber;
			if (!selectedRubber.id) {
				const { data: catalogData } = await trigger({
					stringToSearch: selectedRubber?.name,
				});
				catalogProduct = {
					...catalogData?.payload[0],
					rubber_catalog_id: catalogData?.payload[0].id || 0,
				};

				setValue('custom_rubber', {
					...selectedRubber,
					id: catalogProduct?.id,
					standard_consumption_grams: catalogProduct?.standard_consumption_grams,
				});
			}

			if (selectedNum) {
				setValue(`rubber_plan.NR_grams`, catalogProduct?.standard_consumption_grams, { shouldValidate: true });
			}

			const nextOVorDateSelectOptions = [
				selectedNum?.o_v && { value: 'o_v', label: selectedNum?.o_v },
				selectedNum?.date_of_manufacture && {
					value: 'date_of_manufacture',
					label: dayjs(
						selectedNum?.date_of_manufacture || getValues('rubber_num.date_of_manufacture'),
					).format(DEFAULT_YEAR_FORMAT),
				},
				{ value: 'empty', label: '-' },
			].filter(Boolean) as OVorDateSelectOption[];

			setOVorDateSelectOptions(nextOVorDateSelectOptions);
			setValue(
				'rubber_num',
				{
					id: selectedNum?.id || 0,
					num: selectedNum?.num,
					number_of_melt: selectedNum?.number_of_melt || '-',
					o_v: selectedNum?.o_v || '',
					date_of_manufacture: selectedNum?.date_of_manufacture || '',
					symbol: selectedNum?.symbol || '-',
					sortament: selectedNum?.sortament || '-',
					gost: selectedNum?.gost || '-',
					gost_na_sortament: selectedNum?.gost_na_sortament || '-',
					o_v_or_date_of_manufacture: nextOVorDateSelectOptions[0].value,
					standard_consumption_type: selectedNum?.standard_consumption_type,
				},
				{ shouldValidate: true },
			);
		},
		[nums, selectedRubber, getValues, setValue, toast],
	);

	useEffect(() => {
		if (rubberRef && rubberRef?.current && isCreationMode) {
			(rubberRef.current as HTMLElement).querySelectorAll('input')[0].focus();
		}
	}, [isCreationMode]);

	return (
		<Flex className={styles['main-block']}>
			<Flex direction="column">
				<Flex
					marginTop={'-1px'}
					className={clsx(
						focusedBlock?.block === 'main' &&
							focusedBlock.focusedBy === 'tab' &&
							!focusedBlock.atCurrentBlock &&
							defaultStyles.focused,
					)}
				>
					<Table
						marginRight={'-1px'}
						onFocus={() => setFocusedBlock && setFocusedBlock('main')}
						className={isEditable ? styles['edit-table'] : ''}
					>
						<Tr>
							{/* значение ширины колонки "Маршрутная карта" подобрано вручную для предпросмотра печати */}
							<Th minW="240px" maxW="240px" w="240px" className={styles['without_border_bottom']}>
								Маршрутная карта
							</Th>
							<Th minW="317px" maxW="317px" w="317px" className={styles['without_border_bottom']}>
								Обозначение
							</Th>
						</Tr>
						<Tr>
							<Td>
								{isCreationMode ? (
									<chakra.div display="flex" alignItems="center">
										<chakra.div flex={0.3}>
											<Controller
												name="symbol"
												control={control}
												render={({ field: { onChange, value }, fieldState: { error } }) => {
													return (
														<Input
															flex={0.5}
															tabIndex={1}
															defaultValue={'Р'}
															isInvalid={!!error}
															onChange={(e) => {
																onChange(e.target.value.substring(0, 2));
															}}
															value={value}
															name="symbol"
														/>
													);
												}}
											/>
										</chakra.div>
										-
										<chakra.div flex={1} display="flex" alignItems="center">
											<chakra.p
												style={{
													color: '#A0AEC0',
													fontSize: '10px',
													paddingLeft: '6px',
													textAlign: 'left',
												}}
											>
												Номер будет сформирован после создания
											</chakra.p>
										</chakra.div>
									</chakra.div>
								) : (
									<>
										<Flex
											textAlign={'left'}
											alignItems={'center'}
											className={clsx(styles['print_bold_font'], styles['card-number-wrapper'])}
										>
											<span
												style={{
													marginRight: isEditable ? '36px' : '30px',
													marginLeft: isEditable ? '6px' : '0px',
												}}
											>
												<sup>№</sup>
											</span>

											{isEditable ? (
												<>
													<Controller
														name="symbol"
														control={control}
														render={({
															field: { onChange, value },
															fieldState: { error },
														}) => {
															return (
																<Input
																	flex={1}
																	tabIndex={1}
																	isInvalid={!!error}
																	onChange={onChange}
																	value={value}
																	name="symbol"
																	whiteSpace="36px"
																	maxWidth="36px"
																	maxLength={2}
																/>
															);
														}}
													/>
													-
													<Controller
														name="number"
														control={control}
														render={({
															field: { onChange, value },
															fieldState: { error },
														}) => {
															return (
																<Input
																	flex={1}
																	tabIndex={1}
																	isInvalid={!!error}
																	onChange={onChange}
																	value={value}
																	name="number"
																/>
															);
														}}
													/>
												</>
											) : cardInfo?.number && cardInfo?.symbol ? (
												`${cardInfo.symbol}-${cardInfo.number}`
											) : (
												cardInfo?.number
											)}
										</Flex>
									</>
								)}
							</Td>
							<Td ref={rubberRef}>
								{isEditable && isCreationMode ? (
									<Controller
										name="custom_rubber.symbol"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Select
												status={error ? 'error' : ''}
												tabIndex={1}
												showSearch
												placeholder="Поиск..."
												optionFilterProp="children"
												onChange={(e) => {
													onChange(e);
													handleRubberSelect(e);
												}}
												filterOption={() => true}
												onSearch={(search) => debouncedRubbersSearch(search)}
												value={value}
												options={rubbersQuery ? selectedRubbers : []}
												style={{
													width: '100%',
													maxWidth: '320px',
													boxShadow: error?.message ? '0 0 0 1px #fc5e6e' : '',
												}}
												className={styles['custom-select']}
												notFoundContent={
													<NotFoundContentDropdown
														isFetchingNaming={isRubbersDataFetching}
														alertName={'Введите обозначение'}
													/>
												}
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="300px"
									>
										{cardInfo?.custom_rubber?.symbol}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Td textAlign={'left'}>
								<Flex
									textAlign={'left'}
									alignItems={'center'}
									gap={'10px'}
									className={styles['card-date-wrapper']}
								>
									<span
										style={{
											marginRight: isEditable ? '6px' : '0px',
											marginLeft: isEditable ? '6px' : '0px',
										}}
									>
										<sup>Дата</sup>
									</span>
									{isEditable || isCreationMode ? (
										<Controller
											name="created_at"
											control={control}
											render={({ field: { onChange, value }, fieldState: { error } }) => {
												return (
													<DatePicker
														name="created_at"
														tabIndex={1}
														defaultValue={dayjs(value)}
														disabledDate={(day) => day.isAfter(dayjs(), 'day')}
														format={DEFAULT_YEAR_FORMAT}
														onChange={(date) => onChange(date?.toISOString())}
														status={error ? 'error' : ''}
													/>
												);
											}}
										/>
									) : cardInfo?.created_at ? (
										<span>{dayjs(cardInfo?.created_at).format(DEFAULT_YEAR_FORMAT)}</span>
									) : null}
								</Flex>
							</Td>
							<Td>
								{isEditable ? (
									<Controller
										name="custom_rubber.extra_name"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={2}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.extra_name"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="300px"
									>
										{cardInfo?.custom_rubber?.extra_name || getValues().custom_rubber?.extra_name}
									</Text>
								)}
							</Td>
						</Tr>
					</Table>
					<Table
						onFocus={() => setFocusedBlock && setFocusedBlock('main')}
						className={clsx(isEditable ? styles['edit-table'] : '', styles['right-column-of-main'])}
					>
						<Tr>
							<Th className={styles['without_border_bottom']}>Наименование</Th>
						</Tr>
						<Tr h={'100%'}>
							<Td p="0 !important">
								{isEditable ? (
									<Controller
										name="custom_rubber.name"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={userRole === UserRoles.operator}
												className={
													userRole === UserRoles.operator ? styles['input-readonly'] : ''
												}
												tabIndex={2}
												onChange={onChange}
												value={value}
												name="custom_rubber.name"
											/>
										)}
									/>
								) : (
									<Text className={clsx(styles['text-in-cell-wrap'], styles['print_bold_font'])}>
										{cardInfo?.custom_rubber?.name}
									</Text>
								)}
							</Td>
						</Tr>
						{getValues('custom_rubber.extra_symbol') && (
							<Tr>
								<Td p="0 !important">
									{isEditable ? (
										<Controller
											name="custom_rubber.extra_symbol"
											control={control}
											render={({ field: { onChange, value }, fieldState: { error } }) => (
												<Input
													backgroundColor="transparent"
													isReadOnly={userRole === UserRoles.operator}
													className={
														userRole === UserRoles.operator ? styles['input-readonly'] : ''
													}
													tabIndex={2}
													onChange={onChange}
													value={value}
													name="custom_rubber.extra_symbol"
												/>
											)}
										/>
									) : (
										<Text className={clsx(styles['text-in-cell-wrap'], styles['print_bold_font'])}>
											{cardInfo?.custom_rubber?.extra_symbol}
										</Text>
									)}
								</Td>
							</Tr>
						)}
					</Table>
				</Flex>
				<Flex height={'100%'} marginTop={'-1px'}>
					<Table
						marginRight={'-1px'}
						onFocus={() => setFocusedBlock && setFocusedBlock('material')}
						className={clsx(
							focusedBlock?.block === 'material' &&
								focusedBlock.focusedBy === 'tab' &&
								!focusedBlock.atCurrentBlock &&
								defaultStyles.focused,
							isEditable && styles['edit-table'],
						)}
					>
						<Tr className={styles['slim-row']}>
							<Th w="240px" verticalAlign="middle !important">
								Согласно техн. док.
							</Th>
							<Th verticalAlign="middle !important">Материал</Th>
							<Th verticalAlign="middle !important">Фактически выданный</Th>
						</Tr>
						<Tr>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="custom_rubber.material.symbol"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={userRole === UserRoles.operator}
												className={
													userRole === UserRoles.operator ? styles['input-readonly'] : ''
												}
												tabIndex={2}
												onChange={onChange}
												value={value}
												name="custom_rubber.material.symbol"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="220px"
									>
										{cardInfo?.custom_rubber?.custom_material?.symbol}
									</Text>
								)}
							</Td>
							<Th style={{ verticalAlign: 'middle', lineHeight: '1' }}>Обозначение</Th>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="rubber_num.symbol"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={userRole === UserRoles.operator}
												className={
													userRole === UserRoles.operator ? styles['input-readonly'] : ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="rubber_num.symbol"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="170px"
									>
										{cardInfo?.rubber_num?.symbol}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="custom_rubber.material.gost"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={2}
												onChange={onChange}
												value={value}
												name="custom_rubber.material.gost"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="220px"
									>
										{cardInfo?.custom_rubber?.custom_material?.gost}
									</Text>
								)}
							</Td>
							<Th
								style={{
									verticalAlign: 'middle',
									lineHeight: '1',
								}}
							>
								ТУ / ГОСТ
							</Th>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="rubber_num.gost"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={userRole === UserRoles.operator}
												className={
													userRole === UserRoles.operator ? styles['input-readonly'] : ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="rubber_num.gost"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="170px"
									>
										{cardInfo?.rubber_num?.gost}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="custom_rubber.material.sortament"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={2}
												onChange={onChange}
												value={value}
												name="custom_rubber.material.sortament"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="220px"
									>
										{cardInfo?.custom_rubber?.custom_material?.sortament}
									</Text>
								)}
							</Td>
							<Th style={{ verticalAlign: 'middle', lineHeight: '1' }}>Сортамент</Th>
							<Td style={{ textAlign: 'left' }}>
								{isEditable ? (
									<Controller
										name="rubber_num.sortament"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="rubber_num.sortament"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="170px"
									>
										{cardInfo?.rubber_num?.sortament}
									</Text>
								)}
							</Td>
						</Tr>
					</Table>
					<Table
						onFocus={() => setFocusedBlock && setFocusedBlock('replaceDecision')}
						className={clsx(
							focusedBlock?.block === 'replaceDecision' &&
								focusedBlock.focusedBy === 'tab' &&
								!focusedBlock.atCurrentBlock &&
								defaultStyles.focused,
							isEditable && styles['edit-table'],
							styles['right-column-of-main'],
						)}
					>
						<Tr>
							<Th colSpan={2}>Реш. о замене материала</Th>
						</Tr>
						<Tr>
							<Td colSpan={2}>
								{isEditable ? (
									<Controller
										name="rubber_num.replace_decision"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={4}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="rubber_num.replace_decision"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="220px"
									>
										{cardInfo?.rubber_num?.replace_decision}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Th w="50%" style={{ padding: 0 }}>
								№ плавки/партии
							</Th>
							<Th w="50%" style={{ padding: 0 }}>
								НУМ
							</Th>
						</Tr>
						<Tr>
							<Td>
								{isEditable ? (
									<Controller
										name="rubber_num.number_of_melt"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="rubber_num.number_of_melt"
											/>
										)}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="100px"
									>
										{cardInfo?.rubber_num?.number_of_melt}
									</Text>
								)}
							</Td>
							<Td style={{ padding: 0 }}>
								{isEditable ? (
									<Controller
										name="rubber_num.num"
										control={control}
										render={({ field: { value }, fieldState: { error } }) => {
											return (
												<Select
													status={error ? 'error' : ''}
													tabIndex={3}
													showSearch
													placeholder="Поиск..."
													optionFilterProp="children"
													value={value}
													loading={isRubbersCatalogFetching}
													onChange={handleNumSelect}
													filterOption={() => true}
													options={numsQuery ? selectedNum : []}
													onSearch={(search) => debouncedNumsSearch(search)}
													style={{
														width: '100%',
														boxShadow: error?.message ? '0 0 0 1px #fc5e6e' : '',
													}}
													className={styles['custom-select']}
													notFoundContent={
														<NotFoundContentDropdown
															isFetchingNaming={isRubbersDataFetching}
															alertName={'Введите НУМ'}
														/>
													}
												/>
											);
										}}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="100px"
									>
										{cardInfo?.rubber_num?.num}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Th colSpan={2} style={{ padding: 0 }} verticalAlign="middle !important">
								Дата изготовления материала
							</Th>
						</Tr>
						<Tr>
							<Td colSpan={2}>
								{isEditable ? (
									<Controller
										name="rubber_num.o_v_or_date_of_manufacture"
										render={({ field: { onChange, value } }) => {
											return (
												<Select
													value={value}
													defaultValue={cardInfo?.rubber_num?.o_v_or_date_of_manufacture}
													onChange={onChange}
													options={OVorDateSelectOptions}
													style={{ width: '100%' }}
												/>
											);
										}}
									/>
								) : (
									<Text
										className={clsx(styles['text-in-cell'], styles['print_bold_font'])}
										maxWidth="220px"
									>
										{cardInfo?.rubber_num?.o_v_or_date_of_manufacture === 'empty'
											? '-'
											: cardInfo?.rubber_num?.o_v_or_date_of_manufacture === 'date_of_manufacture'
											? dayjs(cardInfo?.rubber_num?.date_of_manufacture).format(
													DEFAULT_YEAR_FORMAT,
											  )
											: cardInfo?.rubber_num?.o_v}
									</Text>
								)}
							</Td>
						</Tr>
					</Table>
				</Flex>
				<Flex mt="8px">
					<Table className={isEditable ? styles['edit-table'] : ''}>
						<Tr>
							<Th width="25%">Заготовка</Th>
							<Td width="25%">
								{isEditable ? (
									<Controller
										name="custom_rubber.billet"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.billet"
											/>
										)}
									/>
								) : (
									<Text className={clsx(styles['text-in-cell'], styles['print_bold_font'])}>
										{cardInfo?.custom_rubber?.billet}
									</Text>
								)}
							</Td>
							<Th width="25%">Пресс-форма для вулканизации</Th>{' '}
							<Td width="25%">
								{isEditable ? (
									<Controller
										name="custom_rubber.press_form"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.press_form"
											/>
										)}
									/>
								) : (
									<Text className={clsx(styles['text-in-cell'], styles['print_bold_font'])}>
										{cardInfo?.custom_rubber?.press_form}
									</Text>
								)}
							</Td>
						</Tr>
						<Tr>
							<Th width="25%">Навеска, гр.</Th>
							<Td width="25%">
								{isEditable ? (
									<Controller
										name="custom_rubber.weighed_amount"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.weighed_amount"
											/>
										)}
									/>
								) : (
									<Text className={clsx(styles['text-in-cell'], styles['print_bold_font'])}>
										{cardInfo?.custom_rubber?.weighed_amount}
									</Text>
								)}
							</Td>
							<Th width="25%">Оправка для термостатирования</Th>
							<Td width="25%">
								{isEditable ? (
									<Controller
										name="custom_rubber.thermostating_mandrel"
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												isReadOnly={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
												}
												className={
													userRole === UserRoles.operator ||
													userRole === UserRoles.senior_operator
														? styles['input-readonly']
														: ''
												}
												tabIndex={3}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name="custom_rubber.thermostating_mandrel"
											/>
										)}
									/>
								) : (
									<Text className={clsx(styles['text-in-cell'], styles['print_bold_font'])}>
										{cardInfo?.custom_rubber?.thermostating_mandrel}
									</Text>
								)}
							</Td>
						</Tr>
					</Table>
				</Flex>
			</Flex>
		</Flex>
	);
};
